import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorView } from "@/components/StateViews";
import ScoringConfigEditor from "@/components/scoring/ScoringConfigEditor";
import CustomMetricsPanel from "@/components/scoring/CustomMetricsPanel";
import { Gauge, FunctionSquare, FileUp, Flag, History, Info, Download, Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SCORE_ORDER = ["salesperson_execution", "source_performance", "manager_control", "workload_capacity", "fairness_assignment"];
const SEV_TONE = { critical: "bg-red-500", warning: "bg-amber-500", positive: "bg-emerald-500", info: "bg-sky-400" };

export default function ScoringSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadAll = useCallback(async () => {
    setError("");
    try {
      const [{ data: c }, { data: m }, { data: cm }] = await Promise.all([
        api.get("/scoring/configs"), api.get("/scoring/meta"), api.get("/scoring/custom-metrics"),
      ]);
      const ordered = [...c.data].sort((a, b) => SCORE_ORDER.indexOf(a.key) - SCORE_ORDER.indexOf(b.key));
      setConfigs(ordered); setMeta(m.data); setCustomMetrics(cm.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <Loading label="Loading scoring engine..." />;
  if (error) return <ErrorView message={error} onRetry={loadAll} />;

  const scoreTabs = configs.map((c) => ({ key: c.key, label: c.name.replace(" Score", ""), config: c }));
  const TABS = [
    ...scoreTabs.map((t) => ({ ...t, icon: Gauge })),
    { key: "flags", label: "Flags & Diagnosis", icon: Flag },
    { key: "custom-metrics", label: "Custom Metrics", icon: FunctionSquare },
    { key: "versions", label: "Versions / Import-Export", icon: History },
  ];
  const tabParam = searchParams.get("tab");
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : TABS[0]?.key;
  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });
  const activeScore = scoreTabs.find((t) => t.key === tab);

  return (
    <div className="space-y-6" data-testid="scoring-settings-page">
      <div>
        <h1 className="font-sora text-3xl font-bold text-navy">Scoring</h1>
        <p className="text-sm text-ink/60">Admin-controllable scoring engine — formulas, weights, metrics, benchmarks, thresholds and diagnosis flags. Nothing hardcoded.</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-sky">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`scoring-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 rounded-t-lg px-3.5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "border-b-2 border-loop text-loop" : "text-ink/60 hover:text-navy"}`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {activeScore && <ScoringConfigEditor key={activeScore.config.id} config={activeScore.config} meta={meta} onSaved={loadAll} />}

      {tab === "flags" && <FlagsPanel configs={configs} />}
      {tab === "custom-metrics" && <CustomMetricsPanel meta={meta} metrics={customMetrics} onChanged={loadAll} />}
      {tab === "versions" && <VersionsPanel configs={configs} />}
    </div>
  );
}

function FlagsPanel({ configs }) {
  return (
    <div className="space-y-5" data-testid="flags-panel">
      <p className="text-sm text-ink/60">Every flag/diagnosis the reports can raise, grouped by score. These power the actionable insights in reports (Phase 2/3).</p>
      {configs.map((c) => (
        <div key={c.id} className="rounded-2xl border border-sky/70 bg-white p-4">
          <h3 className="mb-2 font-sora text-sm font-semibold text-navy">{c.name}</h3>
          <div className="space-y-1.5">
            {(c.flag_rules || []).map((f) => (
              <div key={f.key} data-testid={`flagrow-${f.key}`} className="rounded-lg border border-sky/50 bg-mist/20 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${SEV_TONE[f.severity] || "bg-sky-400"}`} />
                  <span className="text-sm font-semibold text-navy">{f.label}</span>
                  <span className="rounded-md bg-white px-1.5 py-0.5 text-[10px] uppercase text-ink/40">{f.severity}</span>
                  <code className="ml-auto rounded bg-white px-1.5 py-0.5 text-[10px] text-ink/50">{f.condition}</code>
                </div>
                {f.diagnosis && <p className="mt-1 text-xs text-ink/55">{f.diagnosis}</p>}
                {f.recommended_action && <p className="mt-0.5 text-xs text-loop">→ {f.recommended_action}</p>}
              </div>
            ))}
            {(c.flag_rules || []).length === 0 && <p className="text-xs text-ink/40">No flags configured.</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function VersionsPanel({ configs }) {
  const [sel, setSel] = useState(configs[0]?.id || "");
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try { const { data } = await api.get(`/scoring/configs/${id}/versions`); setVersions(data.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(sel); }, [sel, load]);

  return (
    <div className="space-y-5" data-testid="versions-panel">
      <ImportExportCard />
      <div className="rounded-2xl border border-sky/70 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="font-sora text-sm font-semibold text-navy">Version history</h3>
          <select data-testid="version-config-select" className="ml-auto h-9 rounded-lg border border-sky px-2 text-sm" value={sel} onChange={(e) => setSel(e.target.value)}>
            {configs.map((c) => <option key={c.id} value={c.id}>{c.name} (v{c.version})</option>)}
          </select>
        </div>
        {loading ? <p className="text-sm text-ink/50">Loading…</p> : versions.length === 0 ? (
          <p className="text-sm text-ink/50">No prior versions yet. Each save / import snapshots the previous version here.</p>
        ) : (
          <div className="space-y-1.5">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center gap-3 rounded-lg border border-sky/50 bg-mist/20 px-3 py-2 text-xs">
                <span className="rounded-md bg-ink/10 px-2 py-0.5 font-semibold text-ink/60">v{v.version}</span>
                <span className="text-ink/55">{(v.saved_at || "").slice(0, 19).replace("T", " ")}</span>
                <span className="ml-auto rounded-md bg-sky/20 px-2 py-0.5 text-ink/50">{v.change_type || "manual edit"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportExportCard() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/scoring/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "scoring_config.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported scoring configuration");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = async () => {
      const text = String(reader.result || "");
      setCsvText(text);
      setBusy(true);
      try { const { data } = await api.post("/scoring/import/preview", { csv: text }); setPreview(data.data); }
      catch (err) { toast.error(apiError(err)); }
      finally { setBusy(false); }
    };
    reader.readAsText(f);
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scoring/import/apply", { csv: csvText });
      if (data.data?.applied) { toast.success(`Applied ${data.data.changes.length} change(s)`); setPreview(null); setCsvText(""); setFileName(""); setTimeout(() => window.location.reload(), 600); }
      else toast.error("Could not apply — fix the errors first");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="import-export-card">
      <h3 className="font-sora text-sm font-semibold text-navy">CSV Export / Import</h3>
      <p className="mt-1 text-sm text-ink/60">Download the full scoring setup (formulas, weights, benchmarks, labels, flags, custom metrics), edit it in Excel/Sheets, and re-upload — changes are validated and previewed before they go live.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={exportCsv} disabled={busy} data-testid="scoring-export-btn" className="flex items-center gap-1.5 rounded-lg border border-sky px-3.5 py-2 text-sm font-semibold text-navy hover:border-loop hover:text-loop disabled:opacity-60">
          <Download className="h-4 w-4" />Export CSV
        </button>
        <label data-testid="scoring-import-label" className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-loop px-3.5 py-2 text-sm font-semibold text-white hover:bg-loophover">
          <Upload className="h-4 w-4" />Import CSV
          <input type="file" accept=".csv,text/csv" className="hidden" data-testid="scoring-import-input" onChange={onFile} />
        </label>
        {fileName && <span className="text-xs text-ink/50">{fileName}</span>}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-loop" />}
      </div>

      {preview && (
        <div className="mt-4 space-y-3" data-testid="import-preview">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 font-semibold ${preview.valid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {preview.valid ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {preview.valid ? "Valid" : `${preview.errors.length} error(s)`}
            </span>
            <span className="text-ink/50">{preview.rows} rows · {preview.changes.length} change(s)</span>
          </div>

          {preview.errors.length > 0 && (
            <div className="space-y-1" data-testid="import-errors">
              {preview.errors.map((er, i) => <p key={i} className="rounded bg-red-50 px-2 py-1 text-xs text-red-600">Row {er.row}: {er.message}</p>)}
            </div>
          )}
          {preview.warnings?.length > 0 && (
            <div className="space-y-1">
              {preview.warnings.map((wn, i) => <p key={i} className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">⚠ {wn}</p>)}
            </div>
          )}
          {preview.changes.length > 0 && (
            <div className="max-h-72 overflow-y-auto rounded-lg border border-sky/50">
              <table className="w-full text-xs">
                <thead className="bg-mist/70 text-left text-ink/50"><tr>{["Config", "Target", "Field", "Old", "New"].map((h) => <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-sky/30">
                  {preview.changes.map((c, i) => (
                    <tr key={i} className="bg-white">
                      <td className="px-2 py-1 font-medium text-navy">{c.config}</td>
                      <td className="px-2 py-1 text-ink/60">{c.target}</td>
                      <td className="px-2 py-1 text-ink/60">{c.field}</td>
                      <td className="px-2 py-1 text-red-500">{String(c.old)}</td>
                      <td className="px-2 py-1 font-semibold text-emerald-600">{String(c.new)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => { setPreview(null); setCsvText(""); setFileName(""); }} className="rounded-lg border border-sky px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-mist">Cancel</button>
            <button onClick={apply} disabled={!preview.valid || busy || !preview.changes.length} data-testid="import-apply-btn" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Confirm & apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
